import { FaroRoutes } from '@grafana/faro-react';
import { InternalErrorPage } from '@pages/500';
import { AccountDisabledPage } from '@pages/Anonymous/AccountDisabled';
import { DesktopSignInPage } from '@pages/Anonymous/DesktopSignIn';
import { RateLimitPage } from '@pages/Anonymous/RateLimit';
import { SSOLogin } from '@pages/Anonymous/SSOLogin';
import { ContactSalesPage } from '@pages/ContactSales';
import { LandingPage } from '@pages/LandingPage';
import { NoOrgsPage } from '@pages/User/NoOrgsPage';
import { AuthProvider } from '@providers/AuthProvider';
import { useInitContext } from '@providers/InitProvider';
import { ModalProvider } from '@providers/ModalProvider';
import { OfflineProvider } from '@providers/OfflineProvider';
import { TransactionProcessingProvider } from '@providers/TransactionProcessingProvider';
import { WebsocketProvider } from '@providers/WebsocketProvider';
import { routes } from '@routes';
import * as Sentry from '@sentry/react';
import { CleanPublicWrapper } from '@wrappers/CleanPublic';
import { GuestAppWrapper } from '@wrappers/GuestApp';
import { GuestRoutes } from '@wrappers/GuestApp/routes';
import { AuthenticatedRoutes } from '@wrappers/routes/AuthenticatedRoutes';
import { AcceptInvitationPage } from 'app/pages/Anonymous/AcceptInvitationPage';
import { OpenInASOneJobPage } from 'app/pages/Anonymous/OpenInASOne';
import { PaymentResult } from 'app/pages/Anonymous/PaymentResult';
import { ShareJobPage } from 'app/pages/Anonymous/ShareJob';
import { Helmet } from 'react-helmet-async';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { Initialization } from './Initialization';
import { PendingLogoutRedirect } from './PendingLogoutRedirect';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const Main = () => {
  const { isInitialized, isAuthenticated } = useInitContext();

  if (!isInitialized) {
    return <Initialization />;
  }

  return (
    <Router>
      <PendingLogoutRedirect />
      <OfflineProvider>
        <AuthProvider>
          <WebsocketProvider>
            <TransactionProcessingProvider>
              <ModalProvider>
                <FaroRoutes routesComponent={SentryRoutes}>
                  <Route path={routes.paymentSuccess.pattern} element={<PaymentResult isFailure={false} />} />
                  <Route path={routes.paymentFailure.pattern} element={<PaymentResult isFailure={true} />} />

                  <Route path={routes.acceptInvitation.pattern} element={<AcceptInvitationPage />} />

                  <Route path={routes.internalError.pattern} element={<InternalErrorPage />} />

                  <Route
                    path={routes.root.pattern}
                    element={
                      <>
                        <Helmet>
                          <title>AutoScript</title>
                        </Helmet>
                        <LandingPage />
                      </>
                    }
                  />

                  <Route
                    path={routes.contactSales.pattern}
                    element={
                      <>
                        <Helmet>
                          <title>Contact Sales - AutoScript</title>
                        </Helmet>
                        <ContactSalesPage />
                      </>
                    }
                  />

                  <Route element={<GuestAppWrapper />}>
                    <Route
                      path={routes.desktopSignin.pattern}
                      element={
                        <>
                          <Helmet>
                            <title>Sign In - AutoScript</title>
                          </Helmet>
                          <DesktopSignInPage />
                        </>
                      }
                    />

                    <Route
                      path={routes.ssoSignin.pattern}
                      element={
                        <>
                          <Helmet>
                            <title>Login - AutoScript</title>
                          </Helmet>
                          <SSOLogin />
                        </>
                      }
                    />

                    <Route
                      path={routes.accountDisabled.pattern}
                      element={
                        <>
                          <Helmet>
                            <title>Account Disabled - AutoScript</title>
                          </Helmet>
                          <AccountDisabledPage />
                        </>
                      }
                    />

                    <Route
                      path={routes.rateLimit.pattern}
                      element={
                        <>
                          <Helmet>
                            <title>Rate Limit - AutoScript</title>
                          </Helmet>
                          <RateLimitPage />
                        </>
                      }
                    />
                  </Route>

                  <Route element={<CleanPublicWrapper />}>
                    <Route
                      path={routes.shareJob.pattern}
                      element={
                        <>
                          <Helmet>
                            <title>Shared Job - AutoScript</title>
                          </Helmet>
                          <ShareJobPage />
                        </>
                      }
                    />

                    <Route
                      path={routes.noOrgs.pattern}
                      element={
                        <>
                          <Helmet>
                            <title>No Organizations - AutoScript</title>
                          </Helmet>
                          <NoOrgsPage />
                        </>
                      }
                    />

                    <Route
                      path={routes.openInASOne.pattern}
                      element={
                        <>
                          <Helmet>
                            <title>Open AutoScriptOne - AutoScript</title>
                          </Helmet>
                          <OpenInASOneJobPage />
                        </>
                      }
                    />
                  </Route>

                  <Route path="*" element={isAuthenticated ? <AuthenticatedRoutes /> : <GuestRoutes />} />
                </FaroRoutes>
              </ModalProvider>
            </TransactionProcessingProvider>
          </WebsocketProvider>
        </AuthProvider>
      </OfflineProvider>
    </Router>
  );
};
